<template>
  <div class="generate-ride-ai" :class="{ 'generate-ride-ai--rtl': isHebrew }">
    <h2 class="generate-ride-ai__title">{{ t('generateRideWithAiTitle') }}</h2>
    <p class="generate-ride-ai__intro">{{ t('generateRideWithAiIntro') }}</p>

    <label class="generate-ride-ai__field generate-ride-ai__field--destination">
      <span class="generate-ride-ai__label">{{ t('generateRideWithAiDestination') }}</span>
      <input
        v-model="destination"
        type="text"
        class="generate-ride-ai__destination"
        :placeholder="t('generateRideWithAiDestinationPlaceholder')"
      />
      <small class="generate-ride-ai__hint">{{ t('generateRideWithAiDestinationHint') }}</small>
    </label>

    <div class="generate-ride-ai__dates">
      <label class="generate-ride-ai__field">
        <span class="generate-ride-ai__label">{{ t('generateRideWithAiStartDate') }}</span>
        <input v-model="startDate" type="date" class="generate-ride-ai__date" />
      </label>
      <label class="generate-ride-ai__field">
        <span class="generate-ride-ai__label">{{ t('generateRideWithAiEndDate') }}</span>
        <input v-model="endDate" type="date" class="generate-ride-ai__date" />
      </label>
    </div>

    <label class="generate-ride-ai__field">
      <span class="generate-ride-ai__label">{{ t('generateRideWithAiTravelersLabel') }}</span>
      <textarea
        v-model="travelersInfo"
        class="generate-ride-ai__textarea"
        :placeholder="t('generateRideWithAiTravelersPlaceholder')"
        rows="8"
        maxlength="10000"
      ></textarea>
    </label>

    <label class="generate-ride-ai__field">
      <span class="generate-ride-ai__label">{{ t('generateRideWithAiExampleRides') }}</span>
      <input
        v-model="exampleRideIds"
        type="text"
        class="generate-ride-ai__destination"
        :placeholder="t('generateRideWithAiExampleRidesPlaceholder')"
      />
      <small class="generate-ride-ai__hint">{{ t('generateRideWithAiExampleRidesHint') }}</small>
    </label>

    <div v-if="validationError" class="generate-ride-ai__validation">
      &#9888; {{ validationError }}
    </div>

    <div class="generate-ride-ai__actions">
      <button
        class="generate-ride-ai__btn-submit"
        :disabled="submitting || !isValid"
        @click="submit"
      >
        {{ submitting ? t('generateRideWithAiSubmitting') : t('generateRideWithAiSubmit') }}
      </button>
      <button class="generate-ride-ai__btn-back" :disabled="submitting" @click="goBack">
        {{ t('generateRideWithAiBack') }}
      </button>
    </div>

    <div v-if="submitting" class="generate-ride-ai__progress">
      <div class="generate-ride-ai__spinner"></div>
      <div class="generate-ride-ai__progress-info">
        <span>{{ t('generateRideWithAiBuilding') }}</span>
        <span class="generate-ride-ai__progress-elapsed">{{ elapsedLabel }}</span>
        <small class="generate-ride-ai__progress-hint">{{ t('generateRideWithAiBuildingHint') }}</small>
      </div>
    </div>
    <div v-if="errorMessage" class="generate-ride-ai__error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { translationMixin } from '../mixins/translationMixin';
import documentImportApi from '../api/documentImport';
import { pollJobStatus } from '../common/pollJobStatus';
import { resolvePlace } from '../common/resolvePlace';

const JOB_POLL_TIMEOUT_MS = 12 * 60 * 1000;

export default {
  name: 'GenerateRideWithAi',
  mixins: [translationMixin],

  data() {
    return {
      destination: '',
      exampleRideIds: '',
      startDate: '',
      endDate: '',
      travelersInfo: '',
      submitting: false,
      errorMessage: '',
      startedAt: null,
      now: Date.now(),
    };
  },

  computed: {
    ...mapGetters(['apptoken', 'isUserManager']),

    validationError() {
      if (!this.startDate && !this.endDate && !this.destination.trim()) return '';
      if (!this.destination.trim()) return this.t('generateRideWithAiDestinationRequired');
      if (!this.startDate || !this.endDate) return this.t('generateRideWithAiDatesRequired');
      if (this.endDate < this.startDate) return this.t('generateRideWithAiEndBeforeStart');
      return '';
    },

    isValid() {
      return Boolean(this.destination.trim())
        && Boolean(this.startDate)
        && Boolean(this.endDate)
        && !this.validationError;
    },

    elapsedLabel() {
      if (!this.startedAt) return '';
      const secs = Math.floor((this.now - this.startedAt) / 1000);
      const m = Math.floor(secs / 60);
      const s = secs % 60;
      return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
    },
  },

  created() {
    if (!this.isUserManager) {
      this.$router.push('/');
    }
  },

  beforeDestroy() {
    this._destroyed = true;
    this.stopClock();
  },

  methods: {
    goBack() {
      this.$router.push({ name: 'admin_dashboard' });
    },

    async submit() {
      if (!this.isValid || this.submitting) return;

      this.submitting = true;
      this.errorMessage = '';
      this.startedAt = Date.now();
      this._clockTimer = setInterval(() => { this.now = Date.now(); }, 1000);

      const language = this.isHebrew ? 'hebrew' : 'english';

      try {
        // The pool is filtered server-side by distance from the destination, so the
        // destination has to become real coordinates before we can ask for a ride.
        const { place } = await resolvePlace(this.$gmapApiPromiseLazy, this.destination.trim());
        if (this._destroyed) return;
        if (!place || !place.geometry) {
          this.errorMessage = this.t('generateRideWithAiDestinationNotFound');
          return;
        }

        const { data } = await documentImportApi.generateRideFromAttractions(this.apptoken, {
          startDate: this.startDate,
          endDate: this.endDate,
          travelersInfo: this.travelersInfo.trim(),
          language,
          destinationLat: place.geometry.location.lat(),
          destinationLng: place.geometry.location.lng(),
          destinationName: place.formatted_address || place.name || this.destination.trim(),
          exampleRideIds: this.exampleRideIds.trim(),
        });

        if (!data || !data.success) {
          this.errorMessage = this.errorMessageFor(data && data.error);
          return;
        }

        const { status, result } = await pollJobStatus(
          () => documentImportApi.getJobStatus(this.apptoken, data.job_id),
          { timeout: JOB_POLL_TIMEOUT_MS },
        );

        if (this._destroyed) return;

        if (status === 'timeout') {
          this.errorMessage = this.t('generateRideWithAiTimeout');
        } else if (status === 'failed' || status === 'not_found') {
          this.errorMessage = this.t('generateRideWithAiJobFailed');
        } else if (status !== 'complete' || !result || !result.success) {
          this.errorMessage = this.errorMessageFor(result && result.error);
        } else {
          this.$router.push('/single_ride_with_map/' + result.ride_id);
        }
      } catch (e) {
        this.errorMessage = this.t('generateRideWithAiError');
      } finally {
        this.stopClock();
        this.submitting = false;
      }
    },

    errorMessageFor(code) {
      if (code === 'no_attractions_in_pool') return this.t('generateRideWithAiNoAttractions');
      if (code === 'no_attractions_near_destination') {
        return this.t('generateRideWithAiNoAttractionsNearby');
      }
      if (code === 'invalid_destination') return this.t('generateRideWithAiDestinationNotFound');
      if (code === 'ai_planning_failed' || code === 'ai_returned_no_valid_attractions') {
        return this.t('generateRideWithAiPlanningFailed');
      }
      if (code === 'travelers_info_too_long') return this.t('generateRideWithAiTravelersInfoTooLong');
      return this.t('generateRideWithAiError');
    },

    stopClock() {
      clearInterval(this._clockTimer);
      this._clockTimer = null;
    },
  },
};
</script>

<style scoped>
.generate-ride-ai {
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 16px;
  font-size: 1.6rem;
}

.generate-ride-ai--rtl {
  direction: rtl;
}

.generate-ride-ai__title {
  margin-bottom: 12px;
}

.generate-ride-ai__intro {
  color: #666;
  font-size: 0.9em;
  margin-bottom: 24px;
}

.generate-ride-ai__destination {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: inherit;
  width: 100%;
  box-sizing: border-box;
}

.generate-ride-ai__field--destination {
  margin-bottom: 20px;
}

.generate-ride-ai__hint {
  color: #888;
  font-size: 0.8em;
}

.generate-ride-ai__dates {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.generate-ride-ai__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 180px;
}

.generate-ride-ai__label {
  font-size: 0.85em;
  color: #555;
}

.generate-ride-ai__date {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: inherit;
}

.generate-ride-ai__textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: inherit;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.generate-ride-ai__validation {
  background: #fff3cd;
  border: 1px solid #ffe08a;
  border-radius: 4px;
  padding: 10px 12px;
  margin-top: 12px;
  font-size: 0.85em;
}

.generate-ride-ai__actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.generate-ride-ai__btn-submit {
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  background: #2c7be5;
  color: #fff;
  cursor: pointer;
  font-size: inherit;
}

.generate-ride-ai__btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.generate-ride-ai__btn-back {
  padding: 12px 24px;
  border: 1px solid #888;
  border-radius: 4px;
  background: #f5f5f5;
  cursor: pointer;
  font-size: inherit;
}

.generate-ride-ai__progress {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
}

.generate-ride-ai__progress-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.generate-ride-ai__progress-elapsed {
  color: #888;
  font-size: 0.85em;
}

.generate-ride-ai__progress-hint {
  color: #888;
  font-size: 0.8em;
}

.generate-ride-ai__spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #eee;
  border-top-color: #2c7be5;
  border-radius: 50%;
  animation: generate-ride-ai-spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes generate-ride-ai-spin {
  to { transform: rotate(360deg); }
}

.generate-ride-ai__error {
  background: #fdecea;
  border: 1px solid #f5b5b0;
  border-radius: 4px;
  padding: 12px;
  margin-top: 20px;
  font-size: 0.9em;
}
</style>
